import { Package } from '@shared/types';

let lastPushedEvent: { event: string; data: string } | null = null;

const DEFAULT_CURRENCY = 'EUR';

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeEcommerce = (event: string, ecommerce: any) => {
  if (!ecommerce || typeof ecommerce !== 'object') return ecommerce;

  const items = Array.isArray(ecommerce.items)
    ? ecommerce.items.map((item: any) => {
        if (!item || typeof item !== 'object') return item;

        const quantity = toNumber(item.quantity) ?? 1;
        const unitPrice = toNumber(item.price) ?? toNumber(item.value);

        const normalizedItem = {
          ...item,
          quantity,
          ...(unitPrice !== null ? { price: unitPrice } : {}),
        };

        // If discounted and both values are present, expose GA4 item discount.
        const originalPrice = toNumber((item as any).original_price);
        if (
          normalizedItem.discount === undefined &&
          originalPrice !== null &&
          unitPrice !== null &&
          originalPrice > unitPrice
        ) {
          normalizedItem.discount = originalPrice - unitPrice;
        }

        return normalizedItem;
      })
    : undefined;

  const computedValue =
    items?.reduce((sum: number, item: any) => {
      const price = toNumber(item?.price) ?? toNumber(item?.value) ?? 0;
      const quantity = toNumber(item?.quantity) ?? 1;
      return sum + price * quantity;
    }, 0) ?? 0;

  const value = toNumber(ecommerce.value) ?? computedValue;

  const normalized = {
    ...ecommerce,
    currency: ecommerce.currency || DEFAULT_CURRENCY,
    value,
    ...(items ? { items } : {}),
  };

  // Use GA4-friendly value for payment method when possible.
  if (event === 'add_payment_info' && normalized.payment_type === 'pay_with_card') {
    normalized.payment_type = 'Credit Card';
  }

  // If a coupon exists at event level and there is a single item without coupon, propagate it.
  if (normalized.coupon && Array.isArray(normalized.items) && normalized.items.length === 1) {
    const onlyItem = normalized.items[0];
    if (onlyItem && typeof onlyItem === 'object' && !onlyItem.coupon) {
      onlyItem.coupon = normalized.coupon;
    }
  }

  return normalized;
};

export const pushToDataLayer = (event: string, data: any) => {
  window.dataLayer = window.dataLayer || [];

  const normalizedData =
    data && data?.ecommerce
      ? { ...data, ecommerce: normalizeEcommerce(event, data.ecommerce) }
      : data;

  const dataString = JSON.stringify(normalizedData);
  if (lastPushedEvent && lastPushedEvent.event === event && lastPushedEvent.data === dataString) {
    return;
  }

  lastPushedEvent = { event, data: dataString };
  window.dataLayer.push({ event, ...normalizedData });
};

export const clearEcommerce = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
};

export const formatServiceItem = (service: any, variant?: string) => ({
  item_id: service.id,
  // Prioritize specific English fields, fallback to generic name
  item_name: service.nameEn || service.name_en || service.serviceNameEn || service.name,
  item_category: 'Service',
  location_id: 'Tallinn',
  ...(toNumber(service.price) !== null ? { price: toNumber(service.price) } : {}),
  ...(variant && { item_variant: variant }),
});
export const formatPackageItem = (pkg: Package, variant?: string) => ({
  item_id: pkg.id,
  item_name: pkg.name,
  item_category: 'Package',
  location_id: 'Tallinn',
  ...(toNumber((pkg as any).price) !== null ? { price: toNumber((pkg as any).price) } : {}),
  ...(variant && { item_variant: variant }),
});

export const formatSessionBundleItem = (
  sessionBundle: any,
  type: 'Subscription' | 'Session Bundle' | 'Promotion' | 'Gift Card',
  overridePrice?: number
) => ({
  item_id: sessionBundle.id || 'GIFT_CARD_General',
  item_name: sessionBundle.name || 'Gift Card',
  item_category: type,
  price: overridePrice !== undefined ? overridePrice : sessionBundle.price,
  value: overridePrice !== undefined ? overridePrice : sessionBundle.price,
});
